import { Fragment, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@libs/zapi-ui/shadcn";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { Copy, Info, Lock, MoreVertical, Trash2 } from "lucide-react";
import { ProjectIcon } from "@/zapi-frontend/shared/ui/ProjectIconEditor/ProjectIconEditor";
import type { TProject } from "@/zapi-frontend/stores/project";
import type { TOrganizationProjectDimension } from "@/zapi-frontend/shared/types/projects";
import type { TSystemColumn } from "../../lib/portfolioStorage";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { formatPortfolioDate } from "../../lib/formatPortfolioDate";
import { getProjectStatus, getProjectStatusLabel } from "../../lib/projectStatus";
import { ProjectVersionBlock } from "../ProjectVersionBlock";
import { StatusDot } from "./_StatusDot";
import { useTranslation } from "react-i18next";

type TProps = {
  project: TProject;
  visibleDimensions: TOrganizationProjectDimension[];
  hiddenSystemColumns: Set<TSystemColumn>;
  hasEdit: boolean;
  canDuplicateProject: boolean;
  organizationId: number;
  onListInvalidated: () => void;
  onOpenProject: (versionId?: string) => void;
  onDuplicateProject: (id: string) => void;
  onDeleteProject: (id: string) => void;
  isDeleting?: boolean;
};

export function ProjectCard({
  project,
  visibleDimensions,
  hiddenSystemColumns,
  hasEdit,
  canDuplicateProject,
  organizationId,
  onListInvalidated,
  onOpenProject,
  onDuplicateProject,
  onDeleteProject,
  isDeleting
}: TProps) {
  const vals = project.dimensionValues ?? {};
  const desc = project.shortDescription || "";
  const creator = project.createdBy?.trim() || "";
  const dimensionValues = visibleDimensions
    .map((d) => vals[String(d.id)] ?? "")
    .filter(Boolean)
    .slice(0, 3);
  const [selectedVersionId, setSelectedVersionId] = useState(project.id);
  const selectedVersion = project.versions?.find((v) => v.id === selectedVersionId);
  const { t } = useTranslation("projects");
  const status = getProjectStatus(selectedVersion ?? project);
  const statusLabel = getProjectStatusLabel(t, selectedVersion ?? project);
  const showStatus = !hiddenSystemColumns.has("status");
  const showVersion = !hiddenSystemColumns.has("version");
  const hasVersionControl = (project.versions?.length ?? 0) > 0;
  const showModified = !hiddenSystemColumns.has("modified");
  const showModifiedBy = !hiddenSystemColumns.has("modifiedBy");
  const showFooter = showStatus || showVersion || showModified || showModifiedBy;

  return (
    <div
      data-testid="project-card-v2"
      className={cn(
        "flex w-full cursor-pointer flex-col items-start gap-[18px] bg-white p-[18px] shadow-200 transition-colors duration-200 hover:bg-innoveraZinc-50 [&:has(a:hover)]:bg-white [&:has(button:hover)]:bg-white",
        isDeleting && "pointer-events-none opacity-50"
      )}
      onClick={(e) => {
        const t = e.target as HTMLElement;
        if (t.closest("button") || t.closest("a") || t.closest("[data-prevent-row-nav]")) return;
        onOpenProject(selectedVersionId);
      }}
    >
      <div className="flex flex-row items-center gap-[25px] self-stretch">
        <div className="flex min-w-0 flex-1 flex-row items-center gap-4">
          <ProjectIcon
            shape={project.iconShape}
            color={project.iconColor}
            logoUrl={project.logoUrl}
            className="size-[52px]"
          />
          <div className="flex min-w-0 flex-col">
            <div className="flex min-w-0 items-center gap-[7px]">
              <FigmaTypography variant="heading-3" color="zinc900" className="min-w-0 truncate">
                {project.name}
              </FigmaTypography>
              {project.visibility === "private" && (
                <Lock className="h-4 w-4 shrink-0 text-innoveraZinc-400" aria-label={t("card.private")} />
              )}
              {desc || creator ? (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        data-prevent-row-nav
                        className="shrink-0 text-innoveraZinc-400 hover:text-innoveraZinc-600"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Info className="h-[18px] w-[18px]" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="right"
                      sideOffset={8}
                      className="w-[309px] whitespace-pre-wrap rounded-lg border border-innoveraZinc-100 bg-white px-4 py-3 text-[14px] leading-[22px] text-innoveraZinc-900 shadow-200"
                    >
                      {creator && (
                        <div
                          data-testid={`project-${project.id}-creator`}
                          className={cn(desc && "mb-1.5 border-b border-innoveraZinc-100 pb-1.5")}
                        >
                          {t("card.createdBy")} <span className="font-semibold">{creator}</span>
                        </div>
                      )}
                      {desc}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <Info className="h-[18px] w-[18px] shrink-0 text-innoveraZinc-400" />
              )}
            </div>
            {dimensionValues.length > 0 && (
              <div className="flex min-w-0 items-center gap-1.5">
                {dimensionValues.map((value, i) => (
                  <Fragment key={i}>
                    {i > 0 && <span className="h-0.5 w-0.5 shrink-0 rounded-full bg-innoveraZinc-400" />}
                    <FigmaTypography variant="body-2" color="zinc500" className="min-w-0 truncate">
                      {value}
                    </FigmaTypography>
                  </Fragment>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex shrink-0 flex-row items-center gap-2">
          <AppButton
            type="button"
            appearance="outlined"
            color="zinc150"
            data-prevent-row-nav
            onClick={(e) => {
              e.stopPropagation();
              onOpenProject(selectedVersionId);
            }}
          >
            {t("card.open")}
          </AppButton>
          {hasEdit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <AppButton type="button" appearance="text" size="icon" data-prevent-row-nav>
                  <MoreVertical className="h-4 w-4 text-innoveraZinc-500" />
                </AppButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44 p-1">
                {canDuplicateProject && !project.isDraft && (
                  <DropdownMenuItem
                    className="gap-2 text-[14px] text-zinc-900"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDuplicateProject(project.id);
                    }}
                  >
                    <Copy className="h-4 w-4 text-zinc-700" />
                    {t("card.duplicate")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="gap-2 text-[14px] text-[#d93d3d] focus:text-[#d93d3d]"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteProject(project.id);
                  }}
                >
                  <Trash2 className="h-4 w-4 text-[#d93d3d]" />
                  {t("card.delete")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {showFooter && (
      <div className="flex flex-col items-start gap-3.5 self-stretch">
        <div className="h-px self-stretch bg-innoveraZinc-100" />
        <div className="flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-3 self-stretch">
          {showVersion && (
          <div className="flex shrink-0 flex-row items-center gap-4">
            <FigmaTypography variant="body-2" color="zinc500" className="shrink-0 whitespace-nowrap">
              {t("card.version")}
            </FigmaTypography>
            <div
              className={cn(
                // Padding lives on the child so the trigger button covers the whole box and every
                // pixel of it opens the versions popup (INV-2338).
                "flex w-[210px] max-w-full shrink items-center overflow-hidden [&>*]:px-3 [&>*]:py-2",
                "[&>button]:w-full [&>button]:justify-between [&>button]:gap-2",
                hasVersionControl && "rounded-lg border border-innoveraZinc-150 bg-white"
              )}
              data-prevent-row-nav
            >
              <ProjectVersionBlock
                projectId={project.id}
                projectName={project.name}
                projectLogoUrl={project.logoUrl}
                versionGroupId={project.versionGroupId}
                versionGroupNewest={project.versionGroupNewest}
                versions={project.versions ?? []}
                isDraft={project.isDraft}
                hasEdit={hasEdit}
                organizationId={organizationId}
                showOlderCount={false}
                selectedVersionId={selectedVersionId}
                onSelectVersion={setSelectedVersionId}
                onListInvalidated={onListInvalidated}
              />
            </div>
          </div>
          )}

          <div className="ml-auto flex shrink-0 flex-row flex-wrap items-start gap-2">
            {showStatus && (
            <div className="flex w-[145px] flex-col items-start justify-center">
              <FigmaTypography variant="body-2" color="zinc500" className="self-stretch truncate">
                {t("card.status")}
              </FigmaTypography>
              <StatusDot status={status.state}>{statusLabel}</StatusDot>
            </div>
            )}
            {showModified && (
            <div className="flex w-[120px] flex-col items-start justify-center">
              <FigmaTypography variant="body-2" color="zinc500" className="self-stretch truncate">
                {t("card.lastModified")}
              </FigmaTypography>
              <FigmaTypography variant="body-2" color="zinc900" className="self-stretch truncate">
                {formatPortfolioDate(selectedVersion?.lastModified ?? project.lastModified)}
              </FigmaTypography>
            </div>
            )}
            {showModifiedBy && (
            <div className="flex w-[120px] flex-col items-start justify-center">
              <FigmaTypography variant="body-2" color="zinc500" className="self-stretch truncate">
                {t("card.modifiedBy")}
              </FigmaTypography>
              <FigmaTypography variant="body-2" color="zinc900" className="self-stretch truncate">
                {selectedVersion?.modifiedBy || project.modifiedBy || "—"}
              </FigmaTypography>
            </div>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
